import { createLazyComponent, LazyModule, type LazyComponent } from 'owa-bundling';
import type * as lazyIndex from './lazyIndex';
import type { CopilotCardHostProps } from './components/CopilotCardHost';

/**
 * The card's presentation, including React, the markdown renderer and the Copilot UI kit. This
 * is the expensive part of CCF, so nothing here is imported until a card actually exists.
 */
const lazyModule: LazyModule<typeof lazyIndex> = new LazyModule<typeof lazyIndex>(
    () => import(/* webpackChunkName: "CopilotCardView" */ './lazyIndex'),
    {
        name: 'CopilotCardView',
    }
);

/**
 * Mount point for a slot's card.
 *
 * Render it only when `mayHaveCopilotCard(slot)` and `canRenderCopilotCard()` are both true.
 * Those two are eager and between them are the whole cost of having a mount point: gating on
 * them is what keeps this chunk, and the card store behind it, off the wire for users who never
 * see a card. The enclosing component must be a mobx `observer` for the gate to react.
 *
 * Which card to show, and whether there is one for this context at all, is decided inside the
 * host — deliberately, so that asking the precise question stays behind the lazy boundary.
 *
 * ```tsx
 * {mayHaveCopilotCard(mailListHeaderCopilotSlot) && canRenderCopilotCard() && (
 *     <LazyCopilotCardHost
 *         slot={mailListHeaderCopilotSlot}
 *         currentContextKey={tableViewId}
 *     />
 * )}
 * ```
 */
export const LazyCopilotCardHost: LazyComponent<CopilotCardHostProps, void> = createLazyComponent(
    lazyModule,
    (m: typeof lazyIndex) => m.CopilotCardHost
);

export type { CopilotCardHostProps } from './components/CopilotCardHost';
